import { type FC, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { t } from 'i18next';

import { type BingoCompletion } from '@/common/types/bingo';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

const ROW_HEIGHT = 18;
const VISIBLE_ROWS = 3;
const MAX_DISPLAY = 6;

// Interval bounds (ms)
const INTERVAL_MAX = 2500;
const INTERVAL_MIN = 600;

function getDisplayName(entry: BingoCompletion): string {
  if (entry.userName) return `[${entry.userName}]`;
  const name = [entry.firstName, entry.lastName].filter(Boolean).join(' ');

  return `[${name || '???'}]`;
}

function getRewardText(
  type: BingoCompletion['type'],
  cardIndex: number | null,
): string {
  switch (type) {
    case 'row':
      return t('reward line!');
    case 'column':
      return t('reward the column!');
    case 'diagonal':
      return t('reward for diagonal!');
    case 'card':
      return t('card #{{index}}!', { index: cardIndex });
  }
}

// Стабильный ключ для React + дедупа. Берём id из бэка, для legacy-записей
// без id — синтезируем из timestamp+type (вероятность коллизии минимальна,
// и старые записи всё равно вытеснятся за несколько спинов).
type DisplayEntry = BingoCompletion & { _key: string };

const keyOf = (entry: BingoCompletion): string =>
  entry.id ?? `legacy-${entry.timestamp}-${entry.type}`;

const BingoActivityFeed: FC = () => {
  const reduxEntries = useAppSelector((state) => state.bingo.recentCompletions);

  const [displayed, setDisplayed] = useState<DisplayEntry[]>([]);
  const queueRef = useRef<DisplayEntry[]>([]);
  // ID всех записей, которые мы когда-либо видели (в displayed либо в queue).
  // Защита от ретрансляций, упирания в cap=6 в reducer и любых race-conditions.
  const seenKeysRef = useRef<Set<string>>(new Set());
  const seededRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Первый непустой набор данных (после загрузки/refetch) — отрисовываем
    // мгновенно, без drain-анимации. Анимация — только для записей,
    // приходящих в течение сессии.
    if (!seededRef.current) {
      if (reduxEntries.length === 0) return;

      seededRef.current = true;

      const seeded = reduxEntries
        .slice(0, MAX_DISPLAY)
        .map((e) => ({ ...e, _key: keyOf(e) }));

      for (const e of seeded) seenKeysRef.current.add(e._key);
      setDisplayed(seeded);

      return;
    }

    // Все последующие апдейты: ищем записи с неизвестным id.
    // reduxEntries отсортирован newest-first, поэтому идём с конца к началу,
    // чтобы в очередь попали в хронологическом порядке (oldest → newest).
    const newOnes: DisplayEntry[] = [];
    for (let i = reduxEntries.length - 1; i >= 0; i--) {
      const e = reduxEntries[i];
      const _key = keyOf(e);

      if (seenKeysRef.current.has(_key)) continue;
      seenKeysRef.current.add(_key);
      newOnes.push({ ...e, _key });
    }

    if (newOnes.length > 0) queueRef.current.push(...newOnes);
  }, [reduxEntries]);

  // Drain: сдвигаем по одному (oldest-first), prepend в displayed,
  // newest всплывает наверх по мере проигрывания анимаций.
  useEffect(() => {
    const tick = (): void => {
      if (queueRef.current.length > 0) {
        const entry = queueRef.current.shift()!;
        setDisplayed((prev) => [entry, ...prev].slice(0, MAX_DISPLAY));
      }

      const pending = queueRef.current.length;
      const interval =
        pending === 0
          ? INTERVAL_MAX
          : Math.max(INTERVAL_MIN, INTERVAL_MAX - pending * 400);

      timerRef.current = setTimeout(tick, interval);
    };

    timerRef.current = setTimeout(tick, INTERVAL_MAX);

    return (): void => {
      clearTimeout(timerRef.current);
    };
  }, []);

  if (displayed.length === 0) return null;

  const pending = queueRef.current.length;
  const animDuration = pending > 3 ? 0.2 : 0.4;

  return (
    <div className={css.wrapper}>
      <div className={css.track}>
        <AnimatePresence initial={false}>
          {displayed.map((entry, i) => (
            <motion.div
              key={entry._key}
              className={css.row}
              initial={{ opacity: 0, y: -ROW_HEIGHT }}
              animate={{
                opacity: i < VISIBLE_ROWS ? 1 : 0,
                y: 0,
              }}
              exit={{ opacity: 0, y: ROW_HEIGHT }}
              transition={{ duration: animDuration, ease: 'easeOut' }}
              layout
            >
              <span className={css.name}>{getDisplayName(entry)}</span>
              <span className={css.action}>
                {entry.type === 'card' ? t('completed') : t('received')}
              </span>
              <span className={css.reward}>
                {getRewardText(entry.type, entry.cardIndex)}
              </span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export { BingoActivityFeed };
